import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BID_STATUSES, bidStatus } from "@/lib/bidStatus";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { MapPin, Gavel, Calendar, CalendarRange, Truck, BadgeCheck, Trophy, DollarSign, Clock, AlertCircle, X, List, Map as MapIcon, Route, Box, Eye } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { RatingStars } from "@/components/RatingStars";
import { JobsMap } from "@/components/JobsMap";
import { JobsCalendar } from "@/components/JobsCalendar";
import { fmtDuration, fmtDate, addDays, datesBetween, toIso } from "@/lib/format";
import { DatePickerAU, ExpiryPicker } from "@/components/DatePickerAU";
import { BidExtrasEditor } from "@/components/BidExtras";
import { StorageBadge } from "@/components/StorageBadge";
import { StorageQuestionAlert } from "@/components/StorageQuestion";
import { toast } from "sonner";

export function BidDialog({ job, myBid, onDone, open: controlledOpen, onOpenChange, hideTrigger, triggerClassName = "" }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const [amount, setAmount] = useState(myBid?.amount || "");
  const [hours, setHours] = useState(myBid?.estimated_hours || "");
  const [notes, setNotes] = useState(myBid?.notes || "");
  const allowedDates = job.preferred_date
    ? datesBetween(addDays(job.preferred_date, -(job.flex_days_before || 0)), addDays(job.preferred_date, job.flex_days_after || 0))
    : [];
  const initOpts = myBid?.date_options?.length ? myBid.date_options : myBid?.desired_date ? [{ date: myBid.desired_date, amount: myBid.amount }] : [];
  const [dates, setDates] = useState(initOpts.length ? initOpts.map((o) => o.date) : job.preferred_date ? [job.preferred_date.slice(0, 10)] : []);
  const [perDay, setPerDay] = useState(new Set(initOpts.map((o) => o.amount)).size > 1);
  const [prices, setPrices] = useState(Object.fromEntries(initOpts.map((o) => [o.date, o.amount])));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const approved = !!user?.is_verified;
  const [inclusions, setInclusions] = useState({ items: myBid?.inclusions?.items || [], other: myBid?.inclusions?.other || "", storage_details: myBid?.inclusions?.storage_details || "" });
  const [extras, setExtras] = useState(myBid?.extras?.map((x) => ({ ...x })) || []);
  const extrasOk = extras.every((x) => !x.name.trim() || String(x.price).trim() !== "");
  const initExp = myBid?.expires_at ? new Date(myBid.expires_at) : null;
  const [expDate, setExpDate] = useState(initExp ? toIso(initExp) : "");
  const [expTime, setExpTime] = useState(initExp ? `${String(initExp.getHours()).padStart(2, "0")}:${String(initExp.getMinutes()).padStart(2, "0")}` : "17:00");
  const expiresAt = expDate ? new Date(`${expDate}T${expTime || "23:59"}`) : null;
  const expiryOk = !expiresAt || expiresAt > new Date();
  const priceFor = (d) => (perDay && dates.length > 1 ? prices[d] ?? amount : amount);
  const toggleDate = (d) => setDates((ds) => (ds.includes(d) ? ds.filter((x) => x !== d) : [...ds, d].sort()));
  const usesDates = allowedDates.length > 0;
  const ready = approved && extrasOk && expiryOk && hours && (usesDates ? dates.length > 0 && dates.every((d) => parseFloat(priceFor(d)) > 0) : parseFloat(amount) > 0);

  const submit = async () => {
    setError(""); setLoading(true);
    const date_options = usesDates ? dates.map((d) => ({ date: d, amount: parseFloat(priceFor(d)) })) : null;
    const payload = {
      amount: date_options ? Math.min(...date_options.map((o) => o.amount)) : parseFloat(amount),
      estimated_hours: parseFloat(hours), notes, desired_date: dates[0] || "", date_options,
      inclusions, extras: extras.filter((x) => x.name.trim()).map((x) => ({ name: x.name.trim(), price: parseFloat(x.price) })),
      expires_at: expiresAt ? expiresAt.toISOString() : null,
    };
    try {
      if (myBid) {
        await api.put(`/bids/${myBid.id}`, payload);
        toast.success("Bid updated");
      } else {
        await api.post(`/jobs/${job.id}/bids`, payload);
        toast.success("Bid submitted!");
      }
      setOpen(false);
      onDone();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && !approved && !myBid && (
        <Button size="sm" variant="outline" disabled data-testid={`job-card-bid-btn-${job.id}`} className={triggerClassName} title={user?.approval_status === "rejected" ? "Bidding is unavailable for your account" : "Your account is awaiting admin approval"}>{user?.approval_status === "rejected" ? "Bidding unavailable" : "Awaiting approval"}</Button>
      )}
      {!hideTrigger && (approved || myBid) && (
        <DialogTrigger asChild>
          <Button size="sm" data-testid={`job-card-bid-btn-${job.id}`}
            className={`${myBid ? "" : "bg-kinetic hover:bg-kinetic-hover text-white"} ${triggerClassName}`} variant={myBid ? "outline" : "default"}>
            {myBid ? "Edit bid" : "Place bid"}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="bg-card max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{myBid ? "Edit your bid" : "Place a bid"} · {job.title}</DialogTitle>
          <DialogDescription>{allowedDates.length > 1 ? "Pick one or more days you can do the move, set your price, then submit." : "Enter your price and hours, then submit your offer."}</DialogDescription>
        </DialogHeader>
        {error && <div className="flex items-center gap-2 text-sm text-red-600"><AlertCircle className="h-4 w-4" /> {error}</div>}
        {!approved && <div className="flex items-start gap-2 text-sm rounded-lg bg-amber-50 border border-amber-200 text-amber-800 p-3" data-testid="bid-approval-notice"><AlertCircle className="h-4 w-4 shrink-0 mt-0.5" /> {user?.approval_status === "rejected" ? "Your account wasn't approved, so bidding is unavailable." : "Your account is awaiting admin approval. You can bid once MoveTender approves your business."}</div>}
        <div className="space-y-4 py-2">
          {job.preferred_date && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><CalendarRange className="h-4 w-4" /> {allowedDates.length > 1 ? "Days you can move them" : "Move date"}</Label>
              {allowedDates.length > 1 ? (
                <>
                  <p className="text-xs text-muted-foreground">Customer is flexible — select every day that works for you. ★ = their preferred date.</p>
                  <div className="flex flex-wrap gap-2" data-testid="bid-date-options">
                    {allowedDates.map((dt) => (
                      <button key={dt} type="button" data-testid={`bid-date-${dt}`} onClick={() => toggleDate(dt)} aria-pressed={dates.includes(dt)}
                        className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${dates.includes(dt) ? "bg-kinetic text-white border-kinetic" : "border-border hover:border-kinetic/50"}`}>
                        {fmtDate(dt)}{dt === (job.preferred_date || "").slice(0, 10) ? " ★" : ""}
                      </button>
                    ))}
                  </div>
                  {dates.length > 1 && (
                    <label className="flex items-center gap-2 text-sm pt-1">
                      <Switch checked={perDay} onCheckedChange={setPerDay} data-testid="bid-per-day-switch" /> Set a different price for each day
                    </label>
                  )}
                </>
              ) : (
                <p className="text-sm font-medium">{fmtDate(job.preferred_date)} <span className="text-xs text-muted-foreground">(fixed date)</span></p>
              )}
            </div>
          )}
          {perDay && dates.length > 1 ? (
            <div className="space-y-2" data-testid="bid-per-day-prices">
              <Label>Price per day (AUD)</Label>
              {dates.map((d) => (
                <div key={d} className="flex items-center gap-3">
                  <span className="text-sm w-28 shrink-0">{fmtDate(d)}</span>
                  <Input type="number" min="1" value={prices[d] ?? amount} onChange={(e) => setPrices((p) => ({ ...p, [d]: e.target.value }))} placeholder="450" data-testid={`bid-day-price-${d}`} />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Bid amount (AUD){dates.length > 1 ? " — same for all selected days" : ""}</Label>
              <Input type="number" min="1" data-testid="bid-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="450" />
            </div>
          )}
          <div className="space-y-2">
            <Label>Estimated hours</Label>
            <Input type="number" data-testid="bid-hours-input" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="4" />
          </div>
          <BidExtrasEditor inclusions={inclusions} setInclusions={setInclusions} extras={extras} setExtras={setExtras} storageDays={job.move_in_date ? job.storage_days || 0 : 0} />
          <div className="space-y-2">
            <Label>Bid expires (optional)</Label>
            <ExpiryPicker date={expDate} time={expTime} onDate={setExpDate} onTime={setExpTime} min={toIso(new Date())} />
            {!expiryOk && <p className="text-xs text-red-600" data-testid="bid-expiry-error">Expiry must be in the future.</p>}
          </div>
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea data-testid="bid-notes-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="2 movers + truck, includes wrapping…" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading || !ready} data-testid="submit-bid-btn" className="bg-kinetic hover:bg-kinetic-hover text-white">
            {loading ? "Saving…" : myBid ? "Update bid" : "Submit bid"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ordinal = (n) => `${n}${["th", "st", "nd", "rd"][(n % 100 > 10 && n % 100 < 14) || n % 10 > 3 ? 0 : n % 10]}`;

function BidRank({ bid }) {
  if (bid.rank_total == null) return null;
  const s = bid.is_expired ? "expired" : bid.rank === 1 ? "leading" : "placed";
  const total = `${bid.rank_total} bid${bid.rank_total === 1 ? "" : "s"}`;
  return (
    <Badge className={`gap-1.5 whitespace-nowrap ${BID_STATUSES[s].badge}`} data-testid={`my-bid-rank-${bid.id}`}>
      <span className="h-2 w-2 rounded-full" style={{ background: BID_STATUSES[s].colour }} />
      {bid.is_expired ? `Expired · ${total} live` : `${bid.rank === 1 ? "Winning · " : ""}${ordinal(bid.rank)} of ${total}`}
    </Badge>
  );
}

function OpenJobCard({ job, reload }) {
  const navigate = useNavigate();
  const openDetails = () => navigate(`/jobs/${job.id}`);
  const bs = bidStatus(job);
  return (
    <Card data-testid={`open-job-${job.id}`} className="p-5 hover:shadow-lg transition-all">
      <div className="flex items-start justify-between gap-3 mb-3">
        <button onClick={openDetails} data-testid={`open-job-title-${job.id}`} className="text-left font-display font-semibold text-lg leading-tight hover:text-kinetic transition-colors">{job.title}</button>
        {bs !== "none" && (
          <Badge className={`gap-1.5 shrink-0 ${BID_STATUSES[bs].badge}`} data-testid={`open-job-bid-status-${job.id}`}>
            <span className="h-2 w-2 rounded-full" style={{ background: BID_STATUSES[bs].colour }} /> {BID_STATUSES[bs].label}
          </Badge>
        )}
      </div>
      <div className="space-y-1.5 text-sm text-muted-foreground mb-3">
        <p className="flex items-center gap-2 truncate"><MapPin className="h-4 w-4 text-emerald-500 shrink-0" /> {job.pickup_address}</p>
        <p className="flex items-center gap-2 truncate"><MapPin className="h-4 w-4 text-kinetic shrink-0" /> {job.dropoff_address}</p>
        {job.distance_km != null && <p className="flex items-center gap-2"><Route className="h-4 w-4 shrink-0 text-asphalt" /> <span className="font-medium text-asphalt">{job.distance_km} km{job.duration_min != null ? ` · ${fmtDuration(job.duration_min)}` : ""}</span> {job.distance_is_driving ? "by road" : "approx"}</p>}
        {job.preferred_date && <p className="flex items-center gap-2"><Calendar className="h-4 w-4 shrink-0" /> {(job.flex_days_before || job.flex_days_after) ? `${fmtDate(addDays(job.preferred_date, -(job.flex_days_before || 0)))} – ${fmtDate(addDays(job.preferred_date, job.flex_days_after || 0))}` : fmtDate(job.preferred_date)}</p>}
        <StorageBadge job={job} />
      </div>
      {job.inventory?.total_m3 != null && <p className="flex items-center gap-2 text-sm mb-2" data-testid={`job-m3-${job.id}`}><Box className="h-4 w-4 shrink-0 text-kinetic" /> <span className="font-semibold">{job.inventory.total_m3} m³</span> <span className="text-muted-foreground">AI-verified inventory{job.inventory.videos?.length > 1 ? ` · ${job.inventory.videos.length} room videos` : job.inventory.video_path ? " · video" : ""}</span></p>}
      {job.inventory_description && <p className="text-sm text-slate-600 line-clamp-2 mb-3">{job.inventory_description}</p>}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="text-sm">
          <span className="font-mono font-semibold">{job.bid_count}</span> <span className="text-muted-foreground">{job.bid_count === 1 ? "bid" : "bids"}</span>
          {job.lowest_bid != null && <span className="text-muted-foreground"> · low ${job.lowest_bid}</span>}
          {job.my_bid && <span className="block text-xs text-kinetic font-medium">Your bid: {new Set((job.my_bid.date_options || []).map((o) => o.amount)).size > 1 ? "from " : ""}${job.my_bid.amount}</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={openDetails} data-testid={`view-details-btn-${job.id}`} className="gap-1.5">
            <Eye className="h-4 w-4" /> View details
          </Button>
          <BidDialog job={job} myBid={job.my_bid} onDone={reload} />
        </div>
      </div>
    </Card>
  );
}

export default function RemovalistDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openJobs, setOpenJobs] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zonesOnly, setZonesOnly] = useState(false);
  const [params] = useSearchParams();
  const [view, setView] = useState(params.get("view") === "map" ? "map" : "list");
  const [hideBid, setHideBid] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [calBidJob, setCalBidJob] = useState(null);

  const load = () => {
    Promise.all([api.get("/jobs/open", { params: { zones_only: zonesOnly } }), api.get("/bids/mine")]).then(([a, b]) => {
      setOpenJobs(a.data); setMyBids(b.data);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [zonesOnly]);

  const wonBids = myBids.filter((b) => b.status === "accepted");
  const activeBids = myBids.filter((b) => b.status === "pending");

  const jobWindow = (j) => j.preferred_date
    ? { start: addDays(j.preferred_date, -(j.flex_days_before || 0)), end: addDays(j.preferred_date, j.flex_days_after || 0) }
    : null;
  const dateFilterActive = Boolean(dateFrom || dateTo);
  const filteredJobs = openJobs.filter((j) => {
    if (!dateFilterActive) return true;
    const w = jobWindow(j);
    if (!w) return true;
    const from = dateFrom || "0000-01-01";
    const to = dateTo || "9999-12-31";
    return w.start <= to && w.end >= from;
  });

  const withdraw = async (bidId) => {
    try { await api.post(`/bids/${bidId}/withdraw`); toast.success("Bid withdrawn"); load(); }
    catch { toast.error("Failed to withdraw bid"); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <p className="font-mono text-xs text-kinetic uppercase tracking-widest mb-1">Removalist Dashboard</p>
            <h1 className="font-display text-3xl font-bold flex items-center gap-2">
              {user?.company_name || user?.name}
              {user?.is_verified && <BadgeCheck className="h-6 w-6 text-emerald-500" />}
            </h1>
            {user?.rating_count > 0 && <div className="mt-1"><RatingStars value={user.rating} count={user.rating_count} size={16} /></div>}
          </div>
          <Button variant="outline" onClick={() => navigate("/profile")} data-testid="edit-profile-btn" className="gap-2"><Truck className="h-4 w-4" /> Business Profile</Button>
        </div>

        {!user?.is_verified && (
          <Card className={`p-4 mb-6 flex items-start gap-3 ${user?.approval_status === "rejected" ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`} data-testid="approval-banner">
            <AlertCircle className={`h-5 w-5 shrink-0 mt-0.5 ${user?.approval_status === "rejected" ? "text-red-600" : "text-amber-600"}`} />
            <div className="text-sm">
              {user?.approval_status === "rejected" ? (
                <><p className="font-semibold text-red-700">Your account wasn't approved</p><p className="text-red-700/80">You can browse jobs, but bidding is unavailable. Contact MoveTender support if you think this is a mistake.</p></>
              ) : (
                <><p className="font-semibold text-amber-800">Your account is awaiting approval</p><p className="text-amber-800/80">The MoveTender team is checking your business details. You can browse jobs now and start bidding once you're approved. Completing your <b>Business Profile</b> (ABN/licence, website, insurance) helps us approve you faster.</p></>
              )}
            </div>
          </Card>
        )}

        {(!user?.service_zones || user.service_zones.length === 0) && (
          <Card className="p-4 mb-6 bg-kinetic-light border-kinetic/30 flex items-center justify-between gap-4">
            <p className="text-sm text-asphalt">Set your <b>service zones</b> so customers know you cover their area.</p>
            <Button size="sm" variant="outline" onClick={() => navigate("/profile")}>Set zones</Button>
          </Card>
        )}

        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="p-5"><p className="font-mono text-xs text-muted-foreground uppercase">Open jobs</p><p className="font-display text-2xl font-bold">{openJobs.length}</p></Card>
          <Card className="p-5"><p className="font-mono text-xs text-muted-foreground uppercase">Active bids</p><p className="font-display text-2xl font-bold">{activeBids.length}</p></Card>
          <Card className="p-5"><p className="font-mono text-xs text-muted-foreground uppercase">Jobs won</p><p className="font-display text-2xl font-bold text-emerald-600">{wonBids.length}</p></Card>
        </div>

        <Tabs defaultValue="browse">
          <TabsList className="mb-6">
            <TabsTrigger value="browse" data-testid="tab-browse">Browse Jobs ({openJobs.length})</TabsTrigger>
            <TabsTrigger value="mybids" data-testid="tab-my-bids">My Bids ({activeBids.length})</TabsTrigger>
            <TabsTrigger value="won" data-testid="tab-won">Won Jobs ({wonBids.length})</TabsTrigger>
          </TabsList>

          {loading ? <p className="text-muted-foreground">Loading auctions…</p> : (
            <>
              <TabsContent value="browse">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="inline-flex rounded-lg border border-border p-0.5">
                    <button onClick={() => setView("list")} data-testid="view-list-btn" className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 ${view === "list" ? "bg-asphalt text-white" : "text-muted-foreground"}`}><List className="h-4 w-4" /> List</button>
                    <button onClick={() => setView("map")} data-testid="view-map-btn" className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 ${view === "map" ? "bg-asphalt text-white" : "text-muted-foreground"}`}><MapIcon className="h-4 w-4" /> Map</button>
                    <button onClick={() => setView("calendar")} data-testid="view-calendar-btn" className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 ${view === "calendar" ? "bg-asphalt text-white" : "text-muted-foreground"}`}><CalendarRange className="h-4 w-4" /> Calendar</button>
                  </div>
                  <label htmlFor="zones-toggle" className="text-sm text-muted-foreground flex items-center gap-2 cursor-pointer">
                    <MapPin className="h-4 w-4 text-kinetic" /> Only jobs in my service zones
                    <Switch id="zones-toggle" checked={zonesOnly} onCheckedChange={setZonesOnly} data-testid="zones-only-toggle" />
                  </label>
                </div>

                <div className="flex flex-wrap items-end gap-3 mb-5 p-3 rounded-lg border border-border bg-muted/30">
                  <Calendar className="h-4 w-4 text-kinetic mb-2.5" />
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Available from</Label>
                    <DatePickerAU value={dateFrom} onChange={setDateFrom} testid="filter-date-from" small />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Available to</Label>
                    <DatePickerAU value={dateTo} min={dateFrom || undefined} onChange={setDateTo} testid="filter-date-to" small />
                  </div>
                  {dateFilterActive && (
                    <Button variant="ghost" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="clear-date-filter-btn" className="h-9 gap-1 text-muted-foreground"><X className="h-3.5 w-3.5" /> Clear</Button>
                  )}
                  <span className="text-xs text-muted-foreground ml-auto mb-2.5" data-testid="filtered-count">
                    Showing {filteredJobs.length} of {openJobs.length} job{openJobs.length === 1 ? "" : "s"}{dateFilterActive ? " that fit your dates" : ""}
                  </span>
                </div>

                {view === "map" ? (
                  <>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <label htmlFor="hide-bid-toggle" className="text-sm text-muted-foreground flex items-center gap-2 cursor-pointer">
                        <Switch id="hide-bid-toggle" checked={hideBid} onCheckedChange={setHideBid} data-testid="hide-bid-toggle" />
                        Only show jobs I haven't bid on
                      </label>
                      {hideBid && <span className="text-xs text-muted-foreground" data-testid="hide-bid-count">Hiding {filteredJobs.filter((j) => j.my_bid).length} job{filteredJobs.filter((j) => j.my_bid).length === 1 ? "" : "s"} you've bid on</span>}
                    </div>
                    <JobsMap jobs={hideBid ? filteredJobs.filter((j) => !j.my_bid) : filteredJobs} coverage={user?.coverage || []} />
                  </>
                ) : view === "calendar" ? (
                  <JobsCalendar jobs={filteredJobs} onJobClick={(j) => setCalBidJob(j)} />
                ) : (
                  <div data-testid="open-jobs-feed" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredJobs.length ? filteredJobs.map((j) => <OpenJobCard key={j.id} job={j} reload={load} />) : <p className="text-muted-foreground py-10">{dateFilterActive ? "No open jobs match your selected dates. Widen the range or clear the filter." : zonesOnly ? "No open jobs in your service zones. Turn off the filter to see all jobs." : "No open jobs right now. Check back soon."}</p>}
                  </div>
                )}
                {calBidJob && (
                  <BidDialog key={calBidJob.id} job={calBidJob} myBid={calBidJob.my_bid} hideTrigger open onOpenChange={(o) => { if (!o) setCalBidJob(null); }} onDone={() => { setCalBidJob(null); load(); }} />
                )}
              </TabsContent>

              <TabsContent value="mybids">
                <div className="space-y-3">
                  {activeBids.length ? activeBids.map((b) => (
                    <Card key={b.id} data-testid={`my-bid-${b.id}`} className="p-4 flex flex-wrap items-center justify-between gap-4 cursor-pointer hover:border-kinetic" onClick={() => navigate(`/jobs/${b.job_id}`)}>
                      <div>
                        <p className="font-semibold">{b.job?.title}</p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {b.job?.dropoff_address}</p>
                        <StorageBadge job={b.job} className="mt-1" />
                        <StorageQuestionAlert bid={b} />
                      </div>
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                        <BidRank bid={b} />
                        <span className="flex items-center gap-1 font-mono font-semibold"><DollarSign className="h-4 w-4 text-kinetic" />{b.amount}</span>
                        <span className="flex items-center gap-1 text-sm text-muted-foreground"><Clock className="h-4 w-4" />{b.estimated_hours}h</span>
                        <StatusBadge status={b.status} />
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); withdraw(b.id); }} data-testid={`withdraw-${b.id}`} className="gap-1 text-destructive border-destructive/30"><X className="h-3.5 w-3.5" /> Withdraw</Button>
                      </div>
                    </Card>
                  )) : <p className="text-muted-foreground py-10">No active bids. Browse jobs to start bidding.</p>}
                </div>
              </TabsContent>

              <TabsContent value="won">
                <div className="space-y-3">
                  {wonBids.length ? wonBids.map((b) => (
                    <Card key={b.id} data-testid={`won-job-${b.id}`} className="p-4 flex flex-wrap items-center justify-between gap-4 border-emerald-200 bg-emerald-50/40 cursor-pointer" onClick={() => navigate(`/jobs/${b.job_id}`)}>
                      <div className="flex items-center gap-3">
                        <Trophy className="h-5 w-5 text-emerald-600" />
                        <div>
                          <p className="font-semibold">{b.job?.title}</p>
                          <p className="text-sm text-muted-foreground">{b.job?.pickup_address} → {b.job?.dropoff_address}</p>
                          <StorageBadge job={b.job} className="mt-1" />
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-mono font-bold text-emerald-700">${b.amount}</span>
                        <StatusBadge status={b.job?.status} />
                      </div>
                    </Card>
                  )) : <p className="text-muted-foreground py-10">No jobs won yet. Keep bidding!</p>}
                </div>
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>
    </div>
  );
}
